import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  useClient,
  useEmployeeRoles,
  useDistributorEmployees,
  useUpdateEmployeeClientMapping,
  useInvestorProfile,
} from "creso-core/hooks";
import {
  formatAsCrore,
  getInitials,
  isActiveClient,
  shouldEnableRBAC,
  titleCase,
  withOrdinalSuffix,
} from "creso-core/utils";
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "web-design-system";
import {
  ArrowUpRightIcon,
  IdCardIcon,
  LucideUserKey,
  MailIcon,
  PhoneIcon,
  ShieldCheckIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import {
  Avatar,
  ClientStatusBadge,
  AvatarFamily,
  HoldingPatternBadge,
} from "web-core";
import { differenceInYears, formatDistanceToNow, isToday } from "date-fns";
import { CopyableText } from "@core/components/copyable-text";
import { useAuthContext } from "@/providers/auth-provider";
import { handleError } from "@/utils/error";
import { toast } from "sonner";
import { CakeIcon } from "@core/components/icon/cake-icon";
import { startOfDay } from "date-fns/fp";
import { JOINT_CLIENT_HOLDING_PATTERNS } from "creso-core/constants";
import { AddEmail } from "./add-email";
import { ClientContextMenu } from "./client-context-menu";
import {
  AssignEmployeeSelect,
  SELECT_NO_EMPLOYEE_SENTINEL,
} from "./clients/assign-employee-select";
import { ClientNotes } from "./client-notes";
import { ClientTasks } from "./client-tasks";

export function ClientInfo() {
  const { id } = useParams({ strict: false });
  const { user } = useAuthContext();
  const { data: client, refetch } = useClient(Number(id));
  const { data: profile } = useInvestorProfile(
    { investment_account_guid: client?.investment_account ?? "" },
    {
      enabled: Boolean(client?.investment_account),
    }
  );

  const birthdayText = useMemo(() => {
    if (!profile?.date_of_birth) return "";
    const birthDate = new Date(profile?.date_of_birth);
    const birthDayThisYear = new Date(
      new Date().getFullYear(),
      birthDate.getMonth(),
      birthDate.getDate()
    );
    if (!isToday(birthDayThisYear)) return "";
    return `${withOrdinalSuffix(differenceInYears(startOfDay(birthDayThisYear), startOfDay(birthDate)))} birthday today`;
  }, [profile]);

  const isRBACEnabled = useMemo(() => {
    return shouldEnableRBAC(user);
  }, [user]);
  const isPartOfFamily = !!client?.family_name;
  const { data: employeesData } = useDistributorEmployees(
    {
      status: "active",
    },
    { enabled: isRBACEnabled }
  );
  const { data: employeeRoles } = useEmployeeRoles({
    enabled: isRBACEnabled,
  });
  const { mutateAsync: updateEmployeeClientMapping, isPending: isAssigning } =
    useUpdateEmployeeClientMapping();

  const isActiveNow = useMemo(() => {
    if (!client?.last_seen) return false;
    const lastSeenDate = new Date(client?.last_seen);
    return Date.now() - lastSeenDate.getTime() < 2 * 60 * 1000;
  }, [client?.last_seen]);
  const selectedEmployeeId = useMemo(() => {
    if (!client) return null;
    return client.employee_id ?? null;
  }, [client]);
  const employees = employeesData?.results ?? [];
  const roleIdToName = useMemo(() => {
    const map = new Map<number, string>();
    employeeRoles?.results?.forEach((r) => {
      map.set(Number(r.id), r.name);
    });
    return map;
  }, [employeeRoles?.results]);

  const navigate = useNavigate();

  const openUpdateClientDialog = useCallback(() => {
    navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        modal: "client-lead",
        client_id: client?.id,
        action: "update",
      }),
    });
  }, [navigate, client?.id]);

  const handleAssignedToChange = useCallback(
    async (value: string) => {
      if (!client?.id) return;
      const nextEmployeeId =
        value === SELECT_NO_EMPLOYEE_SENTINEL ? null : Number(value);
      if (nextEmployeeId === selectedEmployeeId) return;

      try {
        await updateEmployeeClientMapping({
          payload: {
            employee_id: nextEmployeeId == null ? "" : nextEmployeeId,
            assigned_client_ids: [client.id],
            deassigned_client_ids: [],
          },
        });
        await refetch();
        toast.success("Assigned to updated");
      } catch (error) {
        handleError(error, "Failed to update assignee");
      }
    },
    [client?.id, refetch, selectedEmployeeId, updateEmployeeClientMapping]
  );

  const familyCardContent = (
    <>
      <div className="flex flex-row items-center gap-2">
        <AvatarFamily
          variant="gradient"
          fallback={getInitials(client?.family_name ?? "")}
          url=""
          className="rounded-md"
        />

        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium line-clamp-2">
            {client?.family_name}
          </span>
          {client?.family_aum != null ? (
            <span className="text-xxs text-muted-foreground">
              AUM: {formatAsCrore(client.family_aum, 1)}
            </span>
          ) : null}
        </div>
      </div>
      <ArrowUpRightIcon className="size-5 text-muted-foreground shrink-0" />
    </>
  );
  const familyCard = client?.family_id ? (
    <Link
      to="/clients/families/$id"
      params={{ id: String(client.family_id) }}
      className="flex flex-row items-center gap-2 w-full p-2.5 rounded-lg border border-border bg-background justify-between cursor-pointer !no-underline"
      target="_blank"
    >
      {familyCardContent}
    </Link>
  ) : (
    <div className="flex flex-row items-center gap-2 w-full p-2.5 rounded-lg border border-border bg-background justify-between">
      {familyCardContent}
    </div>
  );

  const assignSelect = (
    <AssignEmployeeSelect
      employees={employees}
      roleIdToName={roleIdToName}
      value={
        selectedEmployeeId == null
          ? SELECT_NO_EMPLOYEE_SENTINEL
          : String(selectedEmployeeId)
      }
      triggerLabel={
        selectedEmployeeId != null
          ? (employees.find((e) => e.id === selectedEmployeeId)?.name ??
            "No one")
          : "No one"
      }
      triggerClassName="h-8  px-0 ml-1.5 gap-1 font-medium border-0 bg-transparent w-fit text-left"
      triggerTextClassName={"text-sm line-clamp-1"}
      disabled={isAssigning || isPartOfFamily}
      onValueChange={handleAssignedToChange}
    />
  );

  return (
    <div className="flex flex-col flex-1">
      <div className="flex flex-col h-full justify-start">
        <Breadcrumb>
          <BreadcrumbList className="gap-0.5">
            <BreadcrumbItem className="text-xs">
              <Link className="text-muted-foreground" to={"/clients"}>
                Clients
              </Link>
            </BreadcrumbItem>
            {client?.name && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem className="text-primary text-xs">
                  {client?.name}
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex flex-col flex-1 mt-6">
          <div className="flex flex-col">
            <div className="flex flex-row w-full justify-between items-center">
              <div className="flex flex-col gap-5 items-start w-full">
                <div className="flex flex-col gap-3 items-start w-full">
                  <div className="relative">
                    <Avatar
                      fallback={getInitials(client?.name ?? "")}
                      url={""}
                    />
                    {isActiveNow && (
                      <div className="size-2 bottom-0 absolute right-0 rounded-full bg-constructive" />
                    )}
                  </div>
                  <div className="flex flex-col w-full">
                    <div className="flex flex-row w-full items-center justify-between">
                      <div className="min-w-0 text-lg font-semibold">
                        <span>{client?.name}</span>
                        {client?.holding_pattern &&
                        JOINT_CLIENT_HOLDING_PATTERNS.includes(
                          client.holding_pattern
                        ) ? (
                          <span className="ml-2 inline-block -translate-y-0.5 align-middle">
                            <HoldingPatternBadge
                              holdingPattern={client?.holding_pattern}
                            />
                          </span>
                        ) : null}
                      </div>
                      <ClientContextMenu className="-mr-2" client={client} />
                    </div>
                    {client?.last_seen ? (
                      <span className="text-xs text-muted-foreground">
                        {isActiveNow
                          ? "Active now"
                          : `last seen ${formatDistanceToNow(client?.last_seen)} ago`}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-row gap-1">
                  <ClientStatusBadge
                    className="text-xs"
                    status={client?.status ?? "LEAD"}
                  />
                  {client?.tax_status_display ? (
                    <Badge
                      variant="custom"
                      className="text-tag-slate-foreground bg-tag-slate"
                    >
                      {client?.tax_status_display}
                    </Badge>
                  ) : null}
                </div>
                {isPartOfFamily && familyCard}
              </div>
            </div>
            <div className="flex flex-col mt-4 gap-4">
              {isRBACEnabled && employees?.length > 0 ? (
                <div className="flex flex-row w-full items-center">
                  <LucideUserKey className="size-4 text-muted-foreground shrink-0" />
                  <span className="text-sm shrink-0 ml-3">Assigned to:</span>
                  {isPartOfFamily ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>{assignSelect}</span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-44">
                        This client is part of a family. Update assignment from
                        the family page.
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    assignSelect
                  )}
                </div>
              ) : null}
              <div className="flex flex-row w-full items-center">
                <ShieldCheckIcon className="size-4 text-muted-foreground shrink-0" />
                <span className="text-sm shrink-0 ml-3">Risk Profile:</span>
                <Button
                  onClick={() =>
                    navigate({
                      to: ".",
                      search: {
                        modal: "risk-profile",
                        client_id: client?.id,
                      },
                    })
                  }
                  className="ml-1.5 h-auto p-0"
                  variant="link"
                >
                  {titleCase(client?.risk_profile ?? "Assess Risk")}
                </Button>
              </div>
            </div>
          </div>
          <div className="h-px w-full mt-5 my-4 bg-border"></div>
          <div className="mt-0 overflow-hidden pt-1">
            <div className="flex flex-col gap-4">
              <div className="flex flex-row w-full gap-3 items-center h-5 overflow-visible">
                <IdCardIcon className="size-4 text-muted-foreground" />
                {!["LEAD", "VERIFICATION_PENDING"].includes(
                  client?.status as string
                ) ? (
                  <CopyableText
                    className="text-sm -ml-1"
                    text={client?.pan ?? ""}
                  />
                ) : (
                  <Button
                    className="h-auto px-0 py-1 text-primary justify-start"
                    variant="link"
                    onClick={openUpdateClientDialog}
                  >
                    {client?.pan || "Add PAN"}
                  </Button>
                )}
              </div>
              <div className="flex flex-row w-full gap-3 items-center h-5 overflow-visible">
                <PhoneIcon className="size-4 text-muted-foreground" />
                {!["LEAD", "VERIFICATION_PENDING"].includes(
                  client?.status as string
                ) ? (
                  <CopyableText
                    className="text-sm -ml-1"
                    text={client?.phone_number ?? ""}
                  />
                ) : (
                  <Button
                    className="h-auto px-0 py-1 text-primary justify-start"
                    variant="link"
                    onClick={openUpdateClientDialog}
                  >
                    {client?.phone_number || "Add phone number"}
                  </Button>
                )}
              </div>
              <div className="flex flex-row w-full gap-3 items-center h-5 overflow-visible">
                <MailIcon className="size-4 shrink-0 text-muted-foreground" />
                {isActiveClient(client) && client?.email ? (
                  <CopyableText
                    className="text-sm -ml-1"
                    text={client?.email ?? ""}
                  />
                ) : (
                  <AddEmail
                    id={Number(id)}
                    contactEmail={client?.email ?? ""}
                  />
                )}
              </div>
              {birthdayText ? (
                <div className="flex flex-row w-full gap-3 items-center h-5 overflow-hidden">
                  <CakeIcon
                    className="size-4"
                    color="var(--name-tag-violet-foreground)"
                  />
                  <span className="font-semibold text-sm bg-linear-to-r from-tag-violet-foreground to-name-tag-rose-foreground bg-clip-text text-transparent inline-block">
                    {birthdayText}
                  </span>
                </div>
              ) : null}
            </div>

            <div className="my-5 h-px w-full bg-border" />

            {client ? (
              <div className="flex flex-col gap-8">
                <ClientNotes client={client} />
                <ClientTasks client={client} />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
